import React, { RefObject, useCallback, useState } from "react";

import { createPortal } from "react-dom";

import { LinkHandler } from "@/components/common/LinkHandler";
import GwpModalContent from "@/components/lp/gwp/Modal/GwpModalContent";
import { ModalNova } from "@/components/ui-kit/ModalNova/ModalNova";
import { TextElementNova } from "@/components/ui-kit/TextElementNova/TextElementNova";
import {
  NO_RIBBONLINK_MEN_PAGES,
  DUO_MEN_PAGES,
  PDP_MEN_PAGES,
  PCP_MEN_PAGES,
  /*
   * PCP_MEN_PAGES,
   * PCP_PAGES,
   */
} from "@/constants";
import { PromoData } from "@/types/promoData";
import { GA4 } from "@/utils/ga4Utils";
import { cn } from "@/utils/general";
import { ThemeTypes } from "@/utils/theming";

import styles from "./RibbonPromo.module.scss";

interface RibbonPromoProps {
  promoCf: PromoData;
  pathname?: string;
  isMobile?: boolean;
  isMouseInHeader?: RefObject<boolean>;
  theme?: ThemeTypes;
}

/**
 ** THIS IS A PROMO RIBBON WITH CONTENTFUL PROMO SOURCE
 *  EXAMPLE https://app.contentful.com/spaces/0rbfqd9c4jdo/environments/staging2/entries/TcBwfsUJ6sUbkDCuivNdx?focusedField=savingsData
 *  IT has NO_RIBBONLINK_PAGES array to disable clicks on specific pages
 *  It can be customized with specific design like "className": "travel-promo" to include planes and other elements
 */

const RibbonPromo: React.FC<RibbonPromoProps> = ({
  isMobile = false,
  pathname = "",
  theme,
  promoCf,
}) => {
  const [isClosed] = useState(false);
  const isMen = theme === "men";
  const id = isMen ? "men" : "women";
  const promoRibbonData = promoCf?.savingsData?.web?.ribbon?.[id];
  const gwpData = promoCf?.savingsData?.web?.gwpData?.[id];
  const tc = promoCf?.tc;
  const [promoModalOpen, setPromoModalOpen] = useState(false);
  // eslint-disable-next-line prefer-const
  let { copy, href, className = "", noLinkPages = null } = promoRibbonData;
  const promoClass =
    "ribbon-promo bg-nova-brand-accent-80 lg:border-t-nova-brand-accent-80 lg:border-b-nova-brand-accent-80 lg:border-r-nova-brand-accent-80";

  /*
   * by default, we have a sitewide NO_RIBBONLINK_PAGES list where ribbon is not clickable,
   * use noLinkPages[] in cf promo data to change it.
   */
  const excludedPages = Array.isArray(noLinkPages) ? noLinkPages : NO_RIBBONLINK_MEN_PAGES;
  const noLinkRoutes = [href, ...excludedPages];

  const isGwpModal =
    gwpData?.items && [...PDP_MEN_PAGES, ...DUO_MEN_PAGES, ...PCP_MEN_PAGES].includes(pathname);

  //Check if the current route matches a no-link route
  if (noLinkRoutes.includes(pathname) && !isGwpModal) {
    href = null;
  }

  const getRibbonAttrs = () => ({
    destination: href,
    text: copy?.replace(/(<([^>]+)>)/gi, ""),
    type: "ribbon",
    module_location: "ribbon",
  });

  const openPromoModal = useCallback(() => {
    if (!gwpData) {
      return;
    }
    setPromoModalOpen(true);
  }, [gwpData]);

  const hidePromoModal = useCallback(() => {
    setPromoModalOpen(false);
  }, []);

  const handleRibbonClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (isGwpModal) {
      e.preventDefault();
      openPromoModal();
    } else {
      GA4.onMarketingCtaClicked(getRibbonAttrs());
    }
  };

  return (
    <div
      style={isClosed ? { display: "none" } : {}}
      className={cn(
        "flex items-center justify-center md:text-center w-full min-h-[32px] relative left-0 border-t lg:h-full lg:rounded-tr-[2px] lg:relative lg:mt-0 lg:top-0 lg:px-3",
        "border-t lg:border-r lg:border-b border-t-nova-transparency-black-20",
        isMobile ? "ribbon-mobile" : "ribbon-desktop",
        pathname.replaceAll("/", "-"),
        promoClass,
        styles["ribbon-promo-container"]
      )}
    >
      {href ? (
        <LinkHandler
          path={href}
          onClick={handleRibbonClick}
          id="ribbon"
          className={cn(
            "flex-1 w-full text-center text-nova-base-black Body-10-Dia-Normal",
            styles["promo-ribbon-text"],
            className ? styles[className] : null,
            styles[id]
          )}
        >
          <span dangerouslySetInnerHTML={{ __html: copy }} />
        </LinkHandler>
      ) : (
        <section
          id="ribbon-no-link"
          className={cn(
            "flex-1 w-full text-center text-nova-base-black Body-10-Dia-Normal",
            styles["promo-ribbon-text"],
            className ? styles[className] : null,
            styles[id]
          )}
        >
          <TextElementNova
            element="p"
            text={copy}
            className={cn(
              "Body-10-Dia-Normal text-center text-nova-base-black w-full [display:initial]"
            )}
          />
        </section>
      )}

      {promoModalOpen && gwpData
        ? createPortal(
            <ModalNova hideModal={hidePromoModal} title={gwpData.title} className="gwp-modal">
              <GwpModalContent data={gwpData} theme={theme} />
              <div className="mt-4 md:mt-10">
                <TextElementNova
                  text={tc}
                  className="Caption-10-Dia-Normal text-nova-transparency-black-60 text-center"
                />
              </div>
            </ModalNova>,
            document.body
          )
        : null}
    </div>
  );
};

export default RibbonPromo;
